import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { type CustomAdventure } from '@/utils/businessAppApi';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { SuperSimpleForm } from '@/components/public/adventures/SuperSimpleForm';
import { PublicPriceCalculator } from '@/components/public/adventures/components/PublicPriceCalculator';
import { AdventureSelectionPanel } from '@/components/public/adventures/components/AdventureSelectionPanel';
import { MobileActionBar } from '@/components/public/adventures/components/MobileActionBar';
import { StickyBusinessHeader } from '@/components/public/adventures/components/StickyBusinessHeader';
import { MainSearchBar } from '@/components/public/adventures/components/MainSearchBar';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useBusinessData } from '@/components/public/adventures/hooks/useBusinessData';
import { useAdventuresData } from '@/components/public/adventures/hooks/useAdventuresData';
import { useFormSubmission } from '@/components/public/adventures/hooks/useFormSubmission';
import { 
  type DateRange,
  sessionStorageUtils
} from '@/components/public/adventures/utils/customAdventureUtils';



// Enhanced Customization Form Panel Component with Integrated Layout
const CustomizeFormPanel = ({
  selectedAdventure,
  resetSelection,
  setSelectedAdventure,
  adventureOptions,
  setAdventureOptions,
  dateRange,
  setDateRange,
  handleFormSubmit,
  handleErrorReset,
  handlePriceUpdate,
  currentTotalPrice,
  currentPriceBreakdown
}: {
  selectedAdventure: CustomAdventure;
  resetSelection: () => void;
  setSelectedAdventure: React.Dispatch<React.SetStateAction<CustomAdventure | null>>;
  adventureOptions: Record<string, any>;
  setAdventureOptions: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  dateRange: DateRange;
  setDateRange: React.Dispatch<React.SetStateAction<DateRange>>;
  handleFormSubmit: (bookingRequest: any) => Promise<void>;
  handleErrorReset: () => void;
  handlePriceUpdate: (totalPrice: number, currency: string, breakdown: Array<{id: string; label: string; description: string; amount: number; type: 'base' | 'option'}>) => void;
  currentTotalPrice?: number;
  currentPriceBreakdown?: Array<{ id?: string; label: string; description?: string; amount: number; type?: 'base' | 'option' }>;
}) => {
  const { t } = useTranslation('public', { keyPrefix: 'customAdventures.form' });

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 h-full">
      {/* Main Form Area */}
      <div className="xl:col-span-2">
        {/* Form Error Boundary */}
        <ErrorBoundary 
          FallbackComponent={({ error, resetErrorBoundary }) => (
            <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg">
              <h3 className="text-lg font-semibold text-amber-800 mb-2">{t('error.title')}</h3>
              <p className="text-amber-700 mb-4">{error.message || t('error.description')}</p>
              <Button 
                onClick={resetErrorBoundary} 
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-100"
              >
                {t('error.action')}
              </Button>
            </div>
          )}
          onReset={() => {
            // Reset form state when error boundary resets
            // Simply re-set the same adventure to trigger re-mount without changing the ID
            if (selectedAdventure) {
              setSelectedAdventure(null);
              setTimeout(() => setSelectedAdventure(selectedAdventure), 0);
            }
          }}
        >
          {selectedAdventure && (
            <div className="custom-adventure-form-container">
              {/* Hero Image Section */}
              {selectedAdventure.images && selectedAdventure.images.length > 0 && (
                <div className="relative h-48 sm:h-64 lg:h-80 rounded-lg overflow-hidden mb-6">
                  <img
                    src={selectedAdventure.images[0]}
                    alt={selectedAdventure.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/50"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h2 className="text-2xl sm:text-3xl font-bold drop-shadow-lg">{selectedAdventure.name}</h2>
                    <p className="text-sm sm:text-base opacity-90 drop-shadow">{selectedAdventure.description}</p>
                  </div>
                </div>
              )}
              
              <ErrorBoundary 
                FallbackComponent={({ error, resetErrorBoundary }) => (
                  <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg">
                    <h3 className="text-lg font-semibold text-amber-800 mb-2">Form Error</h3>
                    <p className="text-amber-700 mb-4">{error.message || 'An error occurred with the booking form'}</p>
                    <Button 
                      onClick={resetErrorBoundary} 
                      variant="outline"
                      className="border-amber-500 text-amber-700 hover:bg-amber-100"
                    >
                      Try Again
                    </Button>
                  </div>
                )}
                onReset={handleErrorReset}
              >
                <SuperSimpleForm 
                  adventure={selectedAdventure}
                  onSubmit={handleFormSubmit}
                  adventureOptions={adventureOptions}
                  onAdventureOptionsChange={setAdventureOptions}
                  onDateRangeChange={setDateRange}
                  currentTotalPrice={currentTotalPrice}
                  currentPriceBreakdown={currentPriceBreakdown}
                />
              </ErrorBoundary>
            </div>
          )}
        </ErrorBoundary>
      </div>
      
      {/* Price Calculator - visible on all screens so price is always calculated for submit */}
      <div className="xl:col-span-1">
        <div className="sticky top-24">
          {selectedAdventure && (
            <PublicPriceCalculator 
              adventure={selectedAdventure}
              selectedOptions={adventureOptions}
              dateRange={dateRange}
              onPriceUpdate={handlePriceUpdate}
            />
          )}
        </div>
      </div>
    </div>
  );
};



export function CustomAdventuresPage() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('public', { keyPrefix: 'customAdventures' });
  
  // Custom hooks
  const { businessData, businessLoading } = useBusinessData(businessId);
  const { adventures, loading, error, pageReady, handleRefresh } = useAdventuresData(businessId);
  
  // Local state
  const [selectedAdventure, setSelectedAdventure] = useState<CustomAdventure | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [bookingData, setBookingData] = useState<{
    adventureId: string;
    options: Record<string, any>;
    totalPrice: number;
    requestNumber?: string;
    status?: string;
  } | null>(null);
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [currentPrice, setCurrentPrice] = useState<{ amount: number; currency: string } | null>(null);
  const [priceBreakdown, setPriceBreakdown] = useState<Array<{id: string; label: string; description: string; amount: number; type: 'base' | 'option'}>>([]);
  const [adventureOptions, setAdventureOptions] = useState<Record<string, any>>({});
  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });

  // Form submission hook
  const { handleFormSubmit, isSubmitting } = useFormSubmission({ 
    selectedAdventure, 
    setBookingData 
  });

  // DEBUG: Log parent state changes
  useEffect(() => {
    console.log('🔄 [CustomAdventures] Parent adventure options changed:', {
      adventureOptions,
      selectedAdventureId: selectedAdventure?.id,
      selectedAdventureName: selectedAdventure?.name
    });
  }, [adventureOptions, selectedAdventure?.id]);

  useEffect(() => {
    console.log('🏔️ [CustomAdventures] Selected adventure changed:', {
      selectedAdventureId: selectedAdventure?.id,
      selectedAdventureName: selectedAdventure?.name,
      currentAdventureOptions: adventureOptions
    });
  }, [selectedAdventure?.id]);
  
  // This ref helps prevent unwanted navigation during component transitions
  const isInitialMount = useRef(true);

  // Load saved adventure from session storage
  useEffect(() => {
    if (!businessId || !pageReady || selectedAdventure) return;
    
    const savedAdventure = sessionStorageUtils.loadSelectedAdventure(businessId);
    if (savedAdventure) {
      setSelectedAdventure(savedAdventure);
    }
    
    isInitialMount.current = false;
  }, [businessId, pageReady, selectedAdventure]);

  // Save selected adventure to session storage
  useEffect(() => {
    if (!isInitialMount.current && selectedAdventure && businessId) {
      sessionStorageUtils.saveSelectedAdventure(selectedAdventure, businessId);
    }
  }, [selectedAdventure, businessId]);

  // Process booking data when it changes
  useEffect(() => {
    if (bookingData) {
      console.log('Booking data updated:', bookingData);
      
      // Here you would typically navigate to a booking form or checkout page
      // For now, we're just showing a success toast
      toast.success('Adventure customized successfully!', {
        description: 'Your preferences have been saved.',
        action: {
          label: 'View Details',
          onClick: () => {
            // Handle viewing booking details
            console.log('View booking details:', {
              adventureId: selectedAdventure?.id,
              options: bookingData.options,
              totalPrice: bookingData.totalPrice
            });
          }
        }
      });
    }
  }, [bookingData, selectedAdventure]);

  const handleAdventureSelect = useCallback((adventure: CustomAdventure) => {
    setSelectedAdventure(adventure);
    
    // Reset form state when switching adventures
    setAdventureOptions({});
    setDateRange({ from: undefined, to: undefined });
    setCurrentPrice(null);
    setPriceBreakdown([]);
    setBookingData(null);
    
    // Save to session storage
    if (businessId) {
      sessionStorageUtils.saveSelectedAdventure(adventure, businessId);
    }
  }, [businessId]);

  const resetSelection = () => {
    setSelectedAdventure(null);
    setBookingData(null);
    setCurrentPrice(null);
    setPriceBreakdown([]);
    sessionStorageUtils.clearSelectedAdventure();
  };

  // Handle price updates from the form
  const handlePriceUpdate = useCallback((totalPrice: number, currency: string, breakdown: Array<{id: string; label: string; description: string; amount: number; type: 'base' | 'option'}>) => {
    setCurrentPrice({ amount: totalPrice, currency });
    setPriceBreakdown(breakdown);
  }, []);

  // Safe navigation back to prevent unexpected route changes
  const handleNavigateBack = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Reset error state if any
    if (error) {
      handleRefresh();
      return;
    }
    
    // If we have a selected adventure, just clear the selection instead of navigating
    if (selectedAdventure) {
      resetSelection();
      return;
    }
    
    // Check if we came from somewhere in the app
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // Fallback to home page if no history
      navigate('/');
    }
  };

  // Utility functions are now imported from @/utils/adventureUtils

  // FIXED: Stable error reset handler to prevent re-mounts
  const handleErrorReset = useCallback(() => {
    console.log('🔄 [CustomAdventureFormWrapper] Error boundary reset triggered');
    // Reset form state when error boundary resets
    // Simply re-set the same adventure to trigger re-mount without changing the ID
    if (selectedAdventure) {
      setSelectedAdventure(null);
      setTimeout(() => setSelectedAdventure(selectedAdventure), 0);
    }
  }, [selectedAdventure]);



  // Handle debug info display
  const renderDebugInfo = useCallback(() => {
    if (!isDebugMode) return null;
    
    return (
      <div className="fixed bottom-4 right-4 bg-black/80 text-white p-4 rounded-lg text-xs font-mono max-w-xs overflow-auto">
        <pre>
          {JSON.stringify({
            businessId,
            adventuresCount: adventures.length,
            selectedAdventureId: selectedAdventure?.id,
            loading,
            error,
            searchQuery,
            pageReady
          }, null, 2)}
        </pre>
      </div>
    );
  }, [businessId, adventures.length, selectedAdventure?.id, loading, error, searchQuery, pageReady, isDebugMode]);

  return (
    <MotionConfig transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1.0] }}>
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
        <Header />
        
        {/* Sticky Business Info Header - Optimized */}
        <StickyBusinessHeader
          businessData={businessData}
          businessLoading={businessLoading}
          businessId={businessId}
        />
        
        {/* Main Content - Adjusted for sticky header */}
        <div className="flex-1 bg-[#FAFAFA]">
          <div className="container mx-auto px-4 pb-20 pt-20">
          
          <MainSearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedAdventure={selectedAdventure}
          />

          {/* Enhanced Responsive Layout: Adventure Selection & Customization */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 h-[calc(100vh-200px)]">
            
            {/* Left Panel: Adventure Selection */}
            <AdventureSelectionPanel
              adventures={adventures}
              selectedAdventure={selectedAdventure}
              loading={loading}
              error={error}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              onAdventureSelect={handleAdventureSelect}
              onRefresh={handleRefresh}
            />
            
            {/* Right Panel: Adventure Customization with Integrated Price Calculator */}
            <AnimatePresence>
              {selectedAdventure && (
                <motion.div 
                  className="lg:col-span-8"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 25 }}
                >
                  <CustomizeFormPanel
                    selectedAdventure={selectedAdventure}
                    resetSelection={resetSelection}
                    setSelectedAdventure={setSelectedAdventure}
                    adventureOptions={adventureOptions}
                    setAdventureOptions={setAdventureOptions}
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    handleFormSubmit={handleFormSubmit}
                    handleErrorReset={handleErrorReset}
                    handlePriceUpdate={handlePriceUpdate}
                    currentTotalPrice={currentPrice?.amount}
                    currentPriceBreakdown={priceBreakdown}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        
        {/* Enhanced Mobile Adventure Selector & Action Bar */}
        <MobileActionBar
          adventures={adventures}
          selectedAdventure={selectedAdventure}
          currentPrice={currentPrice}
          priceBreakdown={priceBreakdown}
          onAdventureSelect={handleAdventureSelect}
          onResetSelection={resetSelection}
        />
        
        {/* Mobile spacing for sticky bar - accounts for adventure switcher + main bar */}
        <div className="h-64 lg:hidden" />
          </div>
        </div>
      {process.env.NODE_ENV === 'development' && renderDebugInfo()}
    </MotionConfig>
  );
} 